import { Box } from "@mui/material";
import { useState } from "react";
import DefaultCausesPanel from "@/components/system/DefaultCausesPanel";
import TermsOfServicePanel from "@/components/system/TermsOfServicePanel";

type SystemTab = "default-causes" | "terms-of-service";

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        appearance: "none",
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "10px 4px",
        marginBottom: -1,
        fontFamily: "var(--font-lato)",
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: "0.2px",
        color: active ? "var(--color-rodeo-black)" : "var(--color-graphite)",
        borderBottom: `2px solid ${active ? "var(--color-dark-dust)" : "transparent"}`,
        transition: "all 0.15s",
      }}
    >
      {label}
    </button>
  );
}

/**
 * Platform settings — GFG's own configuration, not any client's.
 *
 * A tabbed shell, because the things that belong here (rotations, published documents, catalogue
 * toggles, platform parameters) are all "GFG decides this for everyone", and they would otherwise
 * each arrive as another top-level nav item.
 *
 * Gated on modify/CHARITY_DESTINATION at scope "all" — GFG_SUPER_ADMIN only — by both the route and
 * the API.
 */
export default function System() {
  const [tab, setTab] = useState<SystemTab>("default-causes");
  const tabs: { key: SystemTab; label: string }[] = [
    { key: "default-causes", label: "Default Causes" },
    { key: "terms-of-service", label: "Terms of Service" },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{ display: "flex", gap: 3, borderBottom: "1px solid var(--color-card-border)", mb: 3 }}
      >
        {tabs.map((t) => (
          <TabButton
            key={t.key}
            active={tab === t.key}
            label={t.label}
            onClick={() => setTab(t.key)}
          />
        ))}
      </Box>
      {tab === "default-causes" && <DefaultCausesPanel />}
      {tab === "terms-of-service" && <TermsOfServicePanel />}
    </Box>
  );
}
